import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useEventLogging } from '@AppModels/Logging';
import { FileSendLinkPlaceholder } from '@AppModels/Token';
import { useUpdateAutomationStepMutation } from '../../../../queries/Automation/mutations/steps';

interface UseBackupEmailAttachmentSyncProps {
	textingStep: Api.ITextingAutomationStep | null;
	templateId: string | undefined;
	impersonationContext?: Api.IImpersonationContext;
	onSyncSuccess?: (updatedEmailStep: Api.IEmailAutomationStep) => void;
}

function isEmailAutomationStep(step: unknown): step is Api.IEmailAutomationStep {
	return (step as Api.IEmailAutomationStep)?._type === 'EmailAutomationStep';
}

export function useBackupEmailAttachmentSync({
	textingStep,
	templateId,
	impersonationContext,
	onSyncSuccess,
}: UseBackupEmailAttachmentSyncProps) {
	const { logApiError } = useEventLogging('useBackupEmailAttachmentSync');

	const updateEmailStepMutation = useUpdateAutomationStepMutation({
		onError: error => logApiError('SyncBackupEmailAttachments-Error', error),
		onSuccess: (data, _variables) => {
			if (isEmailAutomationStep(data)) {
				onSyncSuccess?.(data);
			}
		},
	});

	const syncToBackupEmail = useCallback(
		(updatedStep: Api.ITextingAutomationStep) => {
			if (!templateId) return;

			const step = textingStep?.conditionalSteps?.[Api.AutomationStepOutcome.Error];
			if (!isEmailAutomationStep(step) || !step.id) return;

			const textingIds = [...(updatedStep.attachments ?? [])]
				.map(a => a.id)
				.sort()
				.join(',');
			const emailIds = [...(step.attachments ?? [])]
				.map(a => a.id)
				.sort()
				.join(',');
			const attachmentsChanged = textingIds !== emailIds;
			const linkFlagChanged = Boolean(updatedStep.sendAttachmentsAsLink) !== Boolean(step.sendAttachmentsAsLink);

			if (!attachmentsChanged && !linkFlagChanged) return;

			let newContent = step.content;
			if (linkFlagChanged) {
				const currentHtml = Api.getContentHtmlStringValueFromRawRichTextContentState(step.content) ?? '';
				const allSymbols = [FileSendLinkPlaceholder.symbol, ...(FileSendLinkPlaceholder.additionalSymbols ?? [])];
				if (updatedStep.sendAttachmentsAsLink) {
					if (!allSymbols.some(sym => currentHtml.includes(sym))) {
						newContent = Api.createRawRichTextContentStateWithHtmlStringValue(
							`${currentHtml}<p>${FileSendLinkPlaceholder.symbol}</p>`
						);
					}
				} else {
					let newHtml = currentHtml;
					for (const sym of allSymbols) {
						newHtml = newHtml.split(`<p>${sym}</p>`).join('').split(sym).join('');
					}
					newContent = Api.createRawRichTextContentStateWithHtmlStringValue(newHtml);
				}
			}

			const updatedEmailStep: Api.IEmailAutomationStep = {
				...step,
				attachments: updatedStep.attachments,
				sendAttachmentsAsLink: updatedStep.sendAttachmentsAsLink,
				content: newContent,
			};

			updateEmailStepMutation.mutate({
				templateId,
				stepId: step.id,
				stepType: Api.AutomationStepType.Email,
				step: updatedEmailStep,
				impersonationContext,
			});
		},
		[textingStep, templateId, impersonationContext, updateEmailStepMutation]
	);

	return { syncToBackupEmail };
}
